import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import guideData from '../data/UserGuide.json';
import '../styles/UserGuide.css';
import DOMPurify from 'dompurify';

const UserGuide = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedModules, setExpandedModules] = useState({});
  const [selectedSection, setSelectedSection] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize expanded state and selected section from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const moduleParam = params.get('module');
    const featureParam = params.get('feature');
    
    if (moduleParam && featureParam) {
      setExpandedModules(prev => ({
        ...prev,
        [moduleParam]: true
      }));
      
      const section = findSectionByModuleAndFeature(moduleParam, featureParam);
      if (section) {
        setSelectedSection(section);

        // Scroll to the selected section
        const sectionId = `${moduleParam.toLowerCase().replace(/\s+/g, '-')}-${featureParam.toLowerCase().replace(/\s+/g, '-')}`;
        // Use setTimeout to ensure DOM is updated before scrolling
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    }
  }, [location]);

  // Helper function to find section by module and feature
  const findSectionByModuleAndFeature = (module, feature) => {
    const moduleData = guideData.find(m => m.module === module);
    if (moduleData) {
      return moduleData.sections.find(s => s.feature === feature);
    }
    return null;
  };

  // Filter data based on search term
  const filteredData = guideData
    .map(module => {
      // Filter sections within the module based on search term
      const filteredSections = module.sections.filter(section =>
        section.feature.toLowerCase().includes(searchTerm.toLowerCase()) ||
        section.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
      // Only include modules that have at least one matching section
      if (filteredSections.length > 0) {
        return {
          ...module,
          sections: filteredSections
        };
      }
      return null;
    })
    .filter(module => module !== null);

  // Toggle module expansion
  const toggleModule = (moduleName) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleName]: !prev[moduleName]
    }));
  };

  // Handle section selection
  const handleSectionSelect = (module, section) => {
    setSelectedSection(section);
    navigate(`/user-guide?module=${encodeURIComponent(module)}&feature=${encodeURIComponent(section.feature)}`);

    // Scroll to the selected section
    const sectionId = `${module.toLowerCase().replace(/\s+/g, '-')}-${section.feature.toLowerCase().replace(/\s+/g, '-')}`;
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="user-guide-container">
      {/* Left Navigation Panel */}
      <div className="nav-panel">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search guides..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button 
            className={`clear-button ${searchTerm ? 'visible' : ''}`}
            onClick={() => setSearchTerm('')}
            aria-label="Clear search"
          >
            ×
          </button>
        </div>
        
        <nav className="module-nav">
          {filteredData.map((module) => (
            <div key={module.module} className="module-section">
              <div 
                className="module-header"
                onClick={() => toggleModule(module.module)}
              >
                <span>{module.module}</span>
                <span className={`expand-icon ${expandedModules[module.module] ? 'expanded' : ''}`}>
                  ▶
                </span>
              </div>
              
              {expandedModules[module.module] && (
                <ul className="feature-list">
                  {module.sections.map((section) => (
                    <li 
                      key={section.feature}
                      className={`feature-item ${selectedSection?.feature === section.feature ? 'selected' : ''}`}
                      onClick={() => handleSectionSelect(module.module, section)}
                    >
                      {section.title}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </nav>
      </div>

      {/* Right Content Panel */}
      <div className="content-panel">
        <div className="introduction-section">
          <h1>User Guide & Video Tutorials</h1>
          <p>Welcome to the Pathshala Sanchalak User Guide. This comprehensive guide contains video tutorials to help you make the most of our platform. Here's how to use this guide:</p>
          <ul className="guide-instructions">
            <li>Use the <strong>search box</strong> on the left to quickly find specific topics</li>
            <li><strong>Navigate through modules</strong> using the sidebar to jump to specific sections</li>
            <li><strong>Scroll through videos</strong> to watch them in sequence</li>
            <li><strong>Share specific tutorials</strong> using the URL that updates as you navigate</li>
          </ul>
          <div className="guide-tip">
            <strong>Pro Tip:</strong> Bookmark this page for quick access to tutorials when you need them.
          </div>
        </div>

        <div className="videos-list">
          {filteredData.map((module) => (
            <div key={module.module} className="module-videos" id={module.module.toLowerCase().replace(/\s+/g, '-')}>
              <h2 className="module-title">{module.module}</h2>
              <div className="videos-section">
                {module.sections.map((section) => (
                  <div 
                    key={section.feature} 
                    className={`video-item ${selectedSection?.feature === section.feature ? 'selected' : ''}`}
                    id={`${module.module.toLowerCase().replace(/\s+/g, '-')}-${section.feature.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="video-details">
                      <h3>{section.title}</h3>
                      <span className="feature-tag">{section.feature}</span>
                    </div>
                    {section.description && (
                      <div className="video-description" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.description) }} />
                    )}
                    <div className="video-player">
                      <video controls>
                        <source src={section.video} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserGuide;